// src/components/ui/ExpandOverlay.tsx
import { useEffect, type ReactNode } from 'react';
import { brand, fonts, motion } from '@/lib/tokens';

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** When true the content area becomes a flex column (no scroll) so children
   *  can fill the full height — used by DraftReplyPanel. Default: false (scrollable). */
  contentFill?: boolean;
};

export function ExpandOverlay({ open, title, subtitle, onClose, children, contentFill = false }: Props) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        // Was hardcoded rgba(0,0,0,0.88) — always a near-black backdrop, even in
        // light mode. Content text uses rgba(var(--fg-rgb),X), which resolves to
        // near-black in light mode too, so text became invisible against this
        // fixed-dark backdrop. Anchoring to --canvas-rgb makes the backdrop
        // follow the active theme (near-white in light, near-black in dark) so
        // it always contrasts correctly with the fg-rgb-based text on top of it.
        background: 'rgba(var(--canvas-rgb),0.92)',
        display: 'flex',
        flexDirection: 'column',
        animation: `overlayEnter ${motion.overlayEnter} both`,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header — fixed 52px */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '10px 20px',
          height: 52,
          borderBottom: `1px solid ${brand.hairlineSoft}`,
          background: brand.surface1,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontFamily: fonts.sans,
              fontSize: 14,
              fontWeight: 600,
              color: brand.onSurface,
              display: 'block',
            }}
          >
            {title}
          </span>
          {subtitle && (
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: 10,
                color: brand.monoDim,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {subtitle}
            </span>
          )}
        </div>

        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: 10,
            color: brand.monoDim,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            whiteSpace: 'nowrap',
          }}
        >
          ESC to close
        </span>

        <button
          onClick={onClose}
          aria-label="Close overlay"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            color: brand.monoDim,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `color ${motion.fast}`,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = brand.onSurface;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = brand.monoDim;
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
            close
          </span>
        </button>
      </div>

      {/* Content area — scrollable OR flex-fill depending on contentFill */}
      {contentFill ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: 24,
          }}
        >
          {children}
        </div>
      ) : (
        <div
          className="custom-scroll"
          style={{ flex: 1, overflowY: 'auto', padding: 24 }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
